import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Trophy, Zap, Shield, Users, X } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [faqData, setFaqData] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [vouches, setVouches] = useState([]);
  const [totalVouches, setTotalVouches] = useState(0);
  const [vouchesLoading, setVouchesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);
  const [activeInfo, setActiveInfo] = useState(null);

  const stats = [
    {
      id: 'years',
      icon: Trophy,
      number: '6+',
      title: 'Years in Business',
      description: 'Serving gamers reliably for over 6 years with consistent results and trusted service.'
    },
    {
      id: 'orders',
      icon: Zap,
      number: '25K+',
      title: 'Orders Completed',
      description: 'With over 25,000 orders completed, our track record reflects reliability, experience, and customer satisfaction.'
    },
    {
      id: 'safety',
      icon: Shield,
      number: '100%',
      title: 'Safe and Secure',
      description: 'Secure systems, privacy-first handling, and account safety on every order.'
    },
    {
      id: 'customers',
      icon: Users,
      number: '18K+',
      title: 'Happy Customers',
      description: 'Trusted by more than 18,000 satisfied customers worldwide.'
    }
  ];

  useEffect(() => {
    fetchFAQ();
    fetchTestimonials();
    fetchVouches();

    // Poll for new vouches every 30 seconds
    const vouchInterval = setInterval(() => {
      fetchVouchesQuiet();
    }, 30000);

    return () => clearInterval(vouchInterval);
  }, []);

  const fetchFAQ = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/faq`);
      setFaqData(response.data.faq);
    } catch (error) {
      console.error('Error fetching FAQ:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/testimonials`);
      setTestimonials(response.data.testimonials);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fetchVouches = async () => {
    setVouchesLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vouches?limit=6&skip=0`);
      setVouches(response.data.vouches || []);
      setTotalVouches(response.data.total || 0);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Error fetching vouches:', error);
    } finally {
      setVouchesLoading(false);
    }
  };

  const fetchVouchesQuiet = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vouches?limit=6&skip=0`);
      setVouches(response.data.vouches || []);
      setTotalVouches(response.data.total || 0);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Error polling vouches:', error);
    }
  };

  const loadMoreVouches = async () => {
    setLoadingMore(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/vouches?limit=6&skip=${vouches.length}`);
      const newVouches = response.data.vouches || [];
      setVouches(prev => [...prev, ...newVouches]);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Error loading more vouches:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-[#f8fafc]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-36 pb-12">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f172a] via-[#0f172a]/90 to-[#0f172a]"></div>
          <img 
            src="/images/hero-bg.jpg"
            alt="Background"
            className="w-full h-full object-cover opacity-15"
            style={{ filter: 'hue-rotate(-10deg) saturate(1.2)' }}
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          {/* Logo */}
          <video 
            src="/images/purerage.webm"
            autoPlay loop muted playsInline
            className="w-[26rem] md:w-[66rem] mx-auto mb-8 drop-shadow-[0_0_40px_rgba(255,0,0,0.7)]"
            data-testid="hero-title"
            style={{ 
              mixBlendMode: 'screen',
              maskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 85%)',
              WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 50%, black 30%, transparent 85%)'
            }}
          />
          
          <p className="text-xl md:text-4xl font-['Bangers'] text-cyan-400 mb-4 tracking-wider drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
            ELITE GAMING SERVICES FOR APEX LEGENDS & BLACK OPS 7
          </p>
          <p className="text-lg md:text-2xl text-amber-400 mb-8 max-w-3xl mx-auto font-['Bangers'] tracking-wider drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
            Professional boosting services for over 6 years. Get your badges, ranks, and camos with zero risk.
          </p>
          
          {/* Description Box */}
          <div className="max-w-5xl mx-auto mb-12 bg-[rgba(30,41,59,0.7)] border-2 border-red-500/50 rounded-2xl p-6 md:p-8 backdrop-blur-xl">
            <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-4">
              AFK Boosts delivers elite boosting services for Apex Legends and Black Ops 7, along with premium Titan 2 weights, settings, and setup services designed to get you up and running the right way. We provide everything needed for a high-level Titan setup, using only the best AI weights and optimized settings available for Apex Legends and Black Ops 7, with more supported games coming soon.
            </p>
            <p className="text-slate-300 text-sm md:text-base leading-relaxed">
              For more than 6 years, AFK Boosts has proudly served the gaming community and completed over 25,000 orders. We are constantly improving our services to stay ahead, while keeping customer satisfaction and account safety as our top priorities. Whether you are looking for boosting, Titan services, or complete setup support, AFK Boosts is the trusted choice for players who want the best.
            </p>
          </div>
          
          {/* Service Buttons with Preview GIFs */}
          <h2 className="text-4xl md:text-6xl tracking-tight mb-8 font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="services-title">
            <span className="text-red-500">START</span> <span className="text-white">YOUR BOOST</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Apex Legends */}
            <div className="flex flex-col items-center">
              <Link 
                to="/apex"
                data-testid="apex-cta-button"
                className="relative overflow-hidden bg-gradient-to-r from-red-700 via-red-500 to-orange-500 text-white font-bold py-4 md:py-5 text-sm md:text-lg uppercase tracking-widest transition-all duration-300 rounded-lg shadow-lg shadow-red-900/50 hover:shadow-red-500/60 hover:scale-105 border border-red-400/30 w-full md:w-[220px] text-center mb-4"
              >
                <span className="relative z-10">APEX LEGENDS</span>
              </Link>
              <Link to="/apex" className="group bg-[rgba(30,41,59,0.7)] border border-red-500 p-2 hover:border-red-400 hover:-translate-y-1 transition-all duration-200 rounded-2xl cursor-pointer backdrop-blur-xl w-full">
                <div className="w-full aspect-video overflow-hidden rounded-lg">
                  <video 
                    src="/images/apex-preview.webm"
                    autoPlay loop muted playsInline
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </Link>
            </div>
            
            {/* Black Ops 7 */}
            <div className="flex flex-col items-center">
              <Link 
                to="/blackops"
                data-testid="blackops-cta-button"
                className="relative overflow-hidden bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-500 text-white font-bold py-4 md:py-5 text-sm md:text-lg uppercase tracking-widest transition-all duration-300 rounded-lg shadow-lg shadow-orange-900/50 hover:shadow-amber-500/60 hover:scale-105 border border-orange-400/30 w-full md:w-[220px] text-center mb-4"
              >
                <span className="relative z-10">BLACK OPS 7</span>
              </Link>
              <Link to="/blackops" className="group bg-[rgba(30,41,59,0.7)] border border-orange-500 p-2 hover:border-orange-400 hover:-translate-y-1 transition-all duration-200 rounded-2xl cursor-pointer backdrop-blur-xl w-full">
                <div className="w-full aspect-video overflow-hidden rounded-lg">
                  <video 
                    src="/images/bo7-preview.webm"
                    autoPlay loop muted playsInline
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </Link>
            </div>
            
            {/* Titan */}
            <div className="flex flex-col items-center">
              <Link 
                to="/titan"
                data-testid="titan-cta-button"
                className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-blue-500 to-cyan-400 text-white font-bold py-4 md:py-5 text-sm md:text-lg uppercase tracking-widest transition-all duration-300 rounded-lg shadow-lg shadow-blue-900/50 hover:shadow-cyan-500/60 hover:scale-105 border border-blue-400/30 w-full md:w-[220px] text-center mb-4"
              >
                <span className="relative z-10">TITAN</span>
              </Link>
              <Link to="/titan" className="group bg-[rgba(30,41,59,0.7)] border border-blue-500 p-2 hover:border-blue-400 hover:-translate-y-1 transition-all duration-200 rounded-2xl cursor-pointer backdrop-blur-xl w-full">
                <div className="w-full aspect-video overflow-hidden rounded-lg">
                  <video 
                    src="/images/titan-preview.webm"
                    autoPlay loop muted playsInline
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </Link>
            </div>
          </div>
          
          <div className="mt-12 animate-bounce">
            <ChevronDown className="w-8 h-8 mx-auto text-red-500" />
          </div>
        </div>
      </section>

      {/* We Make Gaming Easy Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-6xl tracking-tight mb-4 font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="gaming-easy-title">
            <span className="text-red-500">HOW</span> <span className="text-white">IT WORKS</span>
          </h2>
          <p className="text-2xl md:text-3xl text-cyan-400 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] mb-12">
            Click each section for more information
          </p>
          {/* Row 1: Apex buttons */}
          <div className="grid grid-cols-3 gap-3 md:gap-6">
            <div className="bg-[rgba(30,41,59,0.7)] border border-red-500 backdrop-blur-xl p-3 md:p-6 rounded-2xl md:rounded-3xl text-center hover:-translate-y-1 hover:border-red-400 transition-all duration-200 cursor-pointer" data-testid="hiw-apex-lobbies" onClick={() => setActiveInfo(activeInfo === 'badgeLobbies' ? null : 'badgeLobbies')}>
              <h3 className="text-sm md:text-2xl tracking-wider font-['Bangers'] text-white drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Apex Legends:</h3>
              <p className="text-xs md:text-xl tracking-wider font-['Bangers'] text-red-400 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">4K/20 & 10-10-10 Bot Lobbies</p>
            </div>
            <div className="bg-[rgba(30,41,59,0.7)] border border-red-500 backdrop-blur-xl p-3 md:p-6 rounded-2xl md:rounded-3xl text-center hover:-translate-y-1 hover:border-red-400 transition-all duration-200 cursor-pointer" data-testid="hiw-apex-ranked" onClick={() => setActiveInfo(activeInfo === 'ranked' ? null : 'ranked')}>
              <h3 className="text-sm md:text-2xl tracking-wider font-['Bangers'] text-white drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Apex Legends:</h3>
              <p className="text-xs md:text-xl tracking-wider font-['Bangers'] text-red-400 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Ranked Boosts</p>
            </div>
            <div className="bg-[rgba(30,41,59,0.7)] border border-red-500 backdrop-blur-xl p-3 md:p-6 rounded-2xl md:rounded-3xl text-center hover:-translate-y-1 hover:border-red-400 transition-all duration-200 cursor-pointer" data-testid="hiw-apex-predator" onClick={() => setActiveInfo(activeInfo === 'predator' ? null : 'predator')}>
              <h3 className="text-sm md:text-2xl tracking-wider font-['Bangers'] text-white drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Apex Legends:</h3>
              <p className="text-xs md:text-xl tracking-wider font-['Bangers'] text-red-400 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Predator Holds</p>
            </div>
          </div>
          {/* Row 1 Info Boxes */}
          {activeInfo === 'badgeLobbies' && (
            <div className="mt-6 bg-[rgba(30,41,59,0.7)] border border-red-500/30 backdrop-blur-xl p-6 md:p-8 rounded-2xl text-left max-w-4xl mx-auto" data-testid="badge-lobbies-info-box">
              <h4 className="text-xl md:text-2xl text-red-400 font-bold mb-4 font-['Poppins',sans-serif]">Apex Legends: 4K/20 & 10-10-10 Bot Lobbies</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Our AFK Bot Lobbies are Safe, Fast, and Easy! Our lobbies guarantee a 4K Damage and 20 Kill badge for a legend of your choice. No account information required and available on all platforms (PlayStation, Xbox, PC, Nintendo Switch) it doesn't matter you just join the lobby and ready up! You get to go in and kill the bots yourself! The more bot lobbies you order the better discount you will get! Order Now!
              </p>
              <Link to="/apex#badge-bot-lobbies" className="inline-block text-lg md:text-xl tracking-wider font-['Bangers'] text-red-400 hover:text-red-300 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] transition-colors" data-testid="badge-lobbies-info-link">
                View Bot Lobbies →
              </Link>
            </div>
          )}
          {activeInfo === 'ranked' && (
            <div className="mt-6 bg-[rgba(30,41,59,0.7)] border border-red-500/30 backdrop-blur-xl p-6 md:p-8 rounded-2xl text-left max-w-4xl mx-auto" data-testid="ranked-info-box">
              <h4 className="text-xl md:text-2xl text-red-400 font-bold mb-4 font-['Poppins',sans-serif]">Apex Legends: Ranked Boosts</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                The first option is a "Traditional Ranked Boost" where one of our pro boosters get on your account and plays until we reach the rank you ordered.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                The second option is a "Ranked Carry", where you play alongside one of our pro boosters until you reach your selected rank, with no account information required.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                The third option is our "Ranked Bot Lobbies", which also requires no account information and are the fastest way to get the rank you ordered.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Note: Ranked Bot Lobbies are not always available due to constant in-game changes. We work hard to keep them available, but availability cannot always be guaranteed. No matter which option you choose, or which options are available when you order, you will always receive the rank you purchased.
              </p>
              <Link to="/apex#ranked-boosts" className="inline-block text-lg md:text-xl tracking-wider font-['Bangers'] text-red-400 hover:text-red-300 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] transition-colors" data-testid="ranked-info-link">
                View Ranked Boosts →
              </Link>
            </div>
          )}
          {activeInfo === 'predator' && (
            <div className="mt-6 bg-[rgba(30,41,59,0.7)] border border-red-500/30 backdrop-blur-xl p-6 md:p-8 rounded-2xl text-left max-w-4xl mx-auto" data-testid="predator-info-box">
              <h4 className="text-xl md:text-2xl text-red-400 font-bold mb-4 font-['Poppins',sans-serif]">Apex Legends: Predator Holds</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Predator Holds are ranked boosts that take you from your current rank all the way to Top #200 Predator or better. We do not just get you to Predator and stop there — we keep you there for the entire split, guaranteeing you a Top #200 Predator Badge, Frame, and Charm!
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                You can also choose to order a Predator Hold for the full season instead of just one split. The main difference is that a full-season hold gives you the animated Predator badge. If you only purchase one split, you still receive the same rewards, but the badge will not be animated unless you reach Predator in both splits.
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Note: The number shown on your Predator badge will always reflect the highest placement you achieved. For example, if you reach #1 Predator in the first split and #100 Predator in the second split, your badge will show #1.
              </p>
              <Link to="/apex#predator-holds" className="inline-block text-lg md:text-xl tracking-wider font-['Bangers'] text-red-400 hover:text-red-300 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] transition-colors" data-testid="predator-info-link">
                View Predator Holds →
              </Link>
            </div>
          )}

          {/* Row 2: BO7 + Titan buttons */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 mt-6">
            <div className="bg-[rgba(30,41,59,0.7)] border border-orange-500 backdrop-blur-xl p-3 md:p-6 rounded-2xl md:rounded-3xl text-center hover:-translate-y-1 hover:border-orange-400 transition-all duration-200 cursor-pointer" data-testid="hiw-bo7-lobbies" onClick={() => setActiveInfo(activeInfo === 'bo7Lobbies' ? null : 'bo7Lobbies')}>
              <h3 className="text-sm md:text-2xl tracking-wider font-['Bangers'] text-white drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Black Ops 7:</h3>
              <p className="text-xs md:text-xl tracking-wider font-['Bangers'] text-orange-400 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Bot Lobbies</p>
            </div>
            <div className="bg-[rgba(30,41,59,0.7)] border border-orange-500 backdrop-blur-xl p-3 md:p-6 rounded-2xl md:rounded-3xl text-center hover:-translate-y-1 hover:border-orange-400 transition-all duration-200 cursor-pointer" data-testid="hiw-bo7-camos" onClick={() => setActiveInfo(activeInfo === 'mastery' ? null : 'mastery')}>
              <h3 className="text-sm md:text-2xl tracking-wider font-['Bangers'] text-white drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Black Ops 7:</h3>
              <p className="text-xs md:text-xl tracking-wider font-['Bangers'] text-orange-400 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Mastery Camos</p>
            </div>
            <div className="bg-[rgba(30,41,59,0.7)] border border-blue-500 backdrop-blur-xl p-3 md:p-6 rounded-2xl md:rounded-3xl text-center hover:-translate-y-1 hover:border-blue-400 transition-all duration-200 cursor-pointer" data-testid="hiw-titan" onClick={() => setActiveInfo(activeInfo === 'titan' ? null : 'titan')}>
              <h3 className="text-sm md:text-2xl tracking-wider font-['Bangers'] text-white drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Titan:</h3>
              <p className="text-xs md:text-xl tracking-wider font-['Bangers'] text-blue-400 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">Weights, Settings, & Setups</p>
            </div>
          </div>
          {/* Row 2 Info Boxes */}
          {activeInfo === 'bo7Lobbies' && (
            <div className="mt-6 bg-[rgba(30,41,59,0.7)] border border-orange-500/30 backdrop-blur-xl p-6 md:p-8 rounded-2xl text-left max-w-4xl mx-auto" data-testid="bo7-lobbies-info-box">
              <h4 className="text-xl md:text-2xl text-orange-400 font-bold mb-4 font-['Poppins',sans-serif]">Black Ops 7: Bot Lobbies</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Our BO7 bot lobbies are sold by the hour to give you more for your money. We put you one long Domination game so you aren't spending your time in the lobby loading into new games. You can easily unlock all the best rewards and achievements including all the best Gun Skins and calling cards! You can unlock Singularity and other mastery camos in a fraction of the time!
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Rest assured our BO7 Bot Lobbies are 100% safe! So you dont have to worry about shadow bans like you do with our competitors!
              </p>
              <Link to="/blackops#bot-lobbies" className="inline-block text-lg md:text-xl tracking-wider font-['Bangers'] text-orange-400 hover:text-orange-300 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] transition-colors" data-testid="bo7-lobbies-info-link">
                View Bot Lobbies →
              </Link>
            </div>
          )}
          {activeInfo === 'mastery' && (
            <div className="mt-6 bg-[rgba(30,41,59,0.7)] border border-orange-500/30 backdrop-blur-xl p-6 md:p-8 rounded-2xl text-left max-w-4xl mx-auto" data-testid="mastery-info-box">
              <h4 className="text-xl md:text-2xl text-orange-400 font-bold mb-4 font-['Poppins',sans-serif]">Black Ops 7: Mastery Camos</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Mastery Camos are the hardest gun camos to unlock in the entire game! When other players see you with any of these camos they are immediately impressed!
              </p>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Mastery Camo boosts are completed by one of our Professional boosters. We play for you and get the job done quick! So you can kick back and relax and in no time you'll have the Mastery camo you ordered.
              </p>
              <Link to="/blackops#mastery-camos" className="inline-block text-lg md:text-xl tracking-wider font-['Bangers'] text-orange-400 hover:text-orange-300 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] transition-colors" data-testid="mastery-info-link">
                View Mastery Camos →
              </Link>
            </div>
          )}
          {activeInfo === 'titan' && (
            <div className="mt-6 bg-[rgba(30,41,59,0.7)] border border-blue-500/30 backdrop-blur-xl p-6 md:p-8 rounded-2xl text-left max-w-4xl mx-auto" data-testid="titan-info-box">
              <h4 className="text-xl md:text-2xl text-blue-400 font-bold mb-4 font-['Poppins',sans-serif]">Titan: Weights, Settings, & Setups</h4>
              <h4 className="text-xl md:text-2xl text-blue-400 font-bold mb-3 font-['Poppins',sans-serif]">Why Choose Us?</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                AFK Boosts gives customers access to some of the most advanced AI aiming technology available anywhere. When you choose AFK Boosts, you are choosing proven quality, top-tier performance, and a team committed to delivering the best possible results.
              </p>
              <h4 className="text-xl md:text-2xl text-blue-400 font-bold mb-3 font-['Poppins',sans-serif]">What is Titan?</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                When players talk about being "Titan," they are referring to the Titan Two Console Tuner. It's a small adapter box that acts as a middleman between your controller and your console or PC. Think of it as a programmable translator.
              </p>
              <h4 className="text-xl md:text-2xl text-blue-400 font-bold mb-3 font-['Poppins',sans-serif]">vs. Cronus Zen</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                While a Zen runs simple scripts directly on the device, the Titan 2 is used to pass high-speed data from your PC to your controller. This is the key to getting a real AI aimbot on console.
              </p>
              <h4 className="text-xl md:text-2xl text-blue-400 font-bold mb-3 font-['Poppins',sans-serif]">How it Works</h4>
              <p className="text-slate-300 text-sm leading-relaxed mb-5">
                Instead of just running a script, the Titan takes inputs from your computer's AI software and translates them into commands your console understands instantly.
              </p>
              <Link to="/titan#weights" className="inline-block text-lg md:text-xl tracking-wider font-['Bangers'] text-blue-400 hover:text-blue-300 drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] transition-colors" data-testid="titan-info-link">
                View Titan Services →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl mb-4 text-red-500 tracking-wider font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]">
              TRACK RECORD
            </h2>
            <p className="text-2xl md:text-3xl text-cyan-400 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
              Click each section for more information
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat) => {
              const IconComponent = stat.icon;
              return (
                <button
                  key={stat.id}
                  onClick={() => setSelectedStat(stat)}
                  className="text-center p-2 sm:p-3 md:p-8 bg-[rgba(30,41,59,0.7)] border border-white/10 rounded-xl hover:border-red-500/50 hover:bg-[rgba(30,41,59,0.9)] transition-all duration-300 cursor-pointer backdrop-blur-xl"
                  data-testid={`stat-${stat.id}`}
                >
                  <IconComponent className="w-5 h-5 sm:w-6 sm:h-6 md:w-12 md:h-12 text-red-500 mx-auto mb-1 md:mb-4" />
                  <h3 className="text-base sm:text-xl md:text-5xl font-black text-white mb-1 md:mb-2">{stat.number}</h3>
                  <p className="text-slate-400 uppercase tracking-wide text-[7px] sm:text-[9px] md:text-sm leading-tight">{stat.title}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Modal */}
      {selectedStat && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedStat(null)}
        >
          <div 
            className="bg-[#1e293b] border-2 border-red-500/50 max-w-lg w-full p-6 md:p-8 relative rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedStat(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
            
            <div className="flex items-center gap-4 mb-6">
              {React.createElement(selectedStat.icon, {
                className: "w-12 h-12 md:w-16 md:h-16 text-red-500"
              })}
              <div>
                <h3 className="text-3xl md:text-4xl font-black text-white">{selectedStat.number}</h3>
                <p className="text-red-500 uppercase tracking-wider text-sm md:text-base font-bold">{selectedStat.title}</p>
              </div>
            </div>
            
            <p className="text-gray-300 text-base md:text-lg leading-relaxed">
              {selectedStat.description}
            </p>
          </div>
        </div>
      )}

      {/* Customer Feedback / Vouches */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-4xl md:text-6xl tracking-tight mb-4 text-center font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="testimonials-title">
            <span className="text-red-500">REAL CUSTOMER</span> <span className="text-white">FEEDBACK</span>
          </h2>
          
          {/* Button and Live Update Text */}
          <div className="text-center mb-8">
            <p className="text-xl md:text-2xl text-amber-400 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
              Vouches Are Updated Live
            </p>
            {totalVouches > 0 && !vouchesLoading && (
              <p className="text-gray-400 mt-2">{totalVouches} verified vouches</p>
            )}
          </div>
          
          {vouchesLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500"></div>
            </div>
          ) : vouches.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {vouches.map((vouch, index) => (
                  <div 
                    key={vouch.id || index} 
                    className="bg-[rgba(30,41,59,0.7)] border border-white/10 p-6 rounded-lg hover:border-red-500/50 transition-all backdrop-blur-xl"
                    data-testid={`vouch-${index}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      {vouch.author?.avatar_b64 ? (
                        <img 
                          src={vouch.author.avatar_b64} 
                          alt={vouch.author?.username || 'User'}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                          <span className="text-red-500 font-bold">
                            {vouch.author?.username?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-white">{vouch.author?.username || 'Anonymous'}</p>
                        <p className="text-xs text-gray-500">{formatDate(vouch.timestamp)}</p>
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">{vouch.message}</p>
                  </div>
                ))}
              </div>
              {hasMore && (
                <div className="text-center">
                  <button
                    onClick={loadMoreVouches}
                    disabled={loadingMore}
                    className="text-2xl md:text-3xl text-cyan-400 tracking-wider font-['Bangers'] drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)] hover:text-cyan-300 transition-colors disabled:opacity-50"
                    data-testid="load-more-vouches-btn"
                  >
                    {loadingMore ? 'Loading...' : 'Load 6 More Vouches'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-gray-500">No vouches available</p>
          )}
        </div>
      </section>

      {/* FAQ and Connect Section - Side by Side */}
      <section className="py-16 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="grid grid-cols-2 gap-4 md:gap-12">
            {/* FAQ */}
            <div>
              <h2 className="text-3xl md:text-5xl tracking-tight mb-2 font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]" data-testid="faq-title">
                <span className="text-red-500">FAQ</span>
              </h2>
              <p className="text-xl md:text-2xl text-cyan-400 mb-4 md:mb-8 font-['Bangers'] tracking-wider drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
                Click For Answers
              </p>
              <Accordion type="single" collapsible className="space-y-2 md:space-y-3">
                {faqData.map((item, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="bg-[rgba(30,41,59,0.7)] border border-white/10 px-3 md:px-6 hover:border-red-500/30 transition-all rounded-lg backdrop-blur-xl"
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="text-left text-xs md:text-base font-['Rajdhani'] font-semibold hover:text-red-500 py-2 md:py-4">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-400 pb-2 md:pb-4 text-xs md:text-sm">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Connect */}
            <div>
              <h2 className="text-3xl md:text-5xl tracking-tight mb-2 font-['Bangers'] drop-shadow-[3px_3px_0px_rgba(0,0,0,0.8)]">
                <span className="text-red-500">CONNECT</span>
              </h2>
              <p className="text-xl md:text-2xl text-cyan-400 mb-4 md:mb-8 font-['Bangers'] tracking-wider drop-shadow-[2px_2px_0px_rgba(0,0,0,0.8)]">
                Click To Connect
              </p>
              <div className="bg-[rgba(30,41,59,0.7)] border border-white/10 p-4 md:p-8 h-fit rounded-xl backdrop-blur-xl">
                <p className="text-white mb-4 md:mb-6 text-xs md:text-base">
                  Join our community and stay updated with the latest services, promotions, and gaming content.
                </p>
                <div className="grid grid-cols-2 gap-2 md:gap-4">
                  <a 
                    href="https://discord.gg/afkrev" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center md:justify-start gap-2 md:gap-3 bg-[#5865F2] hover:bg-[#4752C4] text-white p-2 md:p-4 transition-all group"
                  >
                    <svg className="w-4 h-4 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    <span className="font-bold uppercase tracking-wider text-xs md:text-base hidden sm:inline">Discord</span>
                  </a>
                  <a 
                    href="https://www.tiktok.com/@afkrevenant" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center md:justify-start gap-2 md:gap-3 bg-black hover:bg-gray-900 text-white p-2 md:p-4 border border-white/20 transition-all group"
                  >
                    <svg className="w-4 h-4 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                    <span className="font-bold uppercase tracking-wider text-xs md:text-base hidden sm:inline">TikTok</span>
                  </a>
                  <a 
                    href="https://www.youtube.com/@AFKREV" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center md:justify-start gap-2 md:gap-3 bg-[#FF0000] hover:bg-[#CC0000] text-white p-2 md:p-4 transition-all group"
                  >
                    <svg className="w-4 h-4 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    <span className="font-bold uppercase tracking-wider text-xs md:text-base hidden sm:inline">YouTube</span>
                  </a>
                  <a 
                    href="https://www.twitch.tv/afkrevenant" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center md:justify-start gap-2 md:gap-3 bg-[#9146FF] hover:bg-[#7B2FFF] text-white p-2 md:p-4 transition-all group"
                  >
                    <svg className="w-4 h-4 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                    </svg>
                    <span className="font-bold uppercase tracking-wider text-xs md:text-base hidden sm:inline">Twitch</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;